import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  GridActionsCellItem,
  GridPaginationModel,
  GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { AccountCircle } from "@mui/icons-material";
import {
  AppSyncSearchQuery,
  AppSyncSearchResult,
} from "@commr/admin/models/common/appSyncSearch";
import { setUserPicture } from "@commr/admin/utils/userUtils";
import { AdminRow } from "@commr/admin/models/user/admin/admin";
import CustomDataGrid from "../common/customDataGrid";

interface Props {
  searchResult?: AppSyncSearchResult;
  searchQuery: AppSyncSearchQuery;
  setSearchQuery: Dispatch<SetStateAction<AppSyncSearchQuery>>;
  isSuper: boolean;
}

const AdminHit: React.FC<React.PropsWithChildren<Props>> = ({
  searchResult,
  searchQuery,
  setSearchQuery,
  isSuper,
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const [adminRows, setAdminRows] = useState<AdminRow[]>([]);

  const setAvatar = (params: GridRenderCellParams) => (
    <Avatar src={params.value?.toString()} />
  );
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: searchQuery.limit,
  });

  const adminsColumns: any = [
    {
      field: "avatar",
      headerName: "",
      flex: 0.1,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<any>) => setAvatar(params),
      renderHeader: () => <AccountCircle />,
    },
    {
      field: "lastName",
      headerName: t("common:lastName"),
      flex: 1,
    },
    {
      field: "firstName",
      headerName: t("common:firstName"),
      flex: 1,
    },
    {
      field: "email",
      headerName: t("common:email"),
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Action",
      type: "actions",
      flex: 0.3,
      align: "center",
      headerAlign: "center",
      getActions: (params: any) => [
        <GridActionsCellItem
          label={t("common:profile")}
          onClick={() =>
            history.push(
              !isSuper
                ? `/tenant/${params.row.tenantID}/residency/${params.row.residencyID}/admin/${params.id}`
                : `/superAdmin/${params.id}`
            )
          }
          showInMenu
        />,
      ],
    },
  ];

  useEffect(() => {
    if (searchResult) {
      setAdminRows([]);
      searchResult.items?.map(async (hit) => {
        const avatar = await setUserPicture(hit.profilePicture);
        const admin: AdminRow = {
          id: hit.id,
          avatar,
          residencyID: hit.residencyID,
          tenantID: hit.tenantID,
          lastName: hit.lastName,
          firstName: hit.firstName,
          email: hit.email,
          createdAt: hit.createdAt,
        };
        setAdminRows((dataGridRows) => [...dataGridRows, admin]);
      });
    }
  }, [searchResult]);

  const handlePaginationModelChange = (
    newPaginationModel: GridPaginationModel
  ) => {
    setPaginationModel(newPaginationModel);
    setSearchQuery((old) => ({
      ...old,
      from: newPaginationModel.page * searchQuery.limit!,
    }));
  };

  const gridProps = {
    pagination: true,
    rowCount: searchResult?.total ?? 0,
    onPaginationModelChange: handlePaginationModelChange,
    paginationModel,
    pageSizeOptions: [],
    rows: adminRows,
    loading: !searchResult,
    columns: adminsColumns,
    paginationMode: "server",
    disableColumnMenu: true,
    disableSelectionOnClick: true,
    disableColumnSelector: true,
    initialState: {
      sorting: {
        sortModel: [{ field: "createdAt", sort: "asc" }],
      },
    },
  };

  return (
    <div style={{ height: 650, width: "100%" }}>
      <CustomDataGrid attribute={gridProps} />
    </div>
  );
};

export default AdminHit;

AdminHit.defaultProps = {
  searchResult: undefined,
};
